import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SEO, breadcrumbSchema, faqSchema } from '../components/SEO';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FAQSection from '../components/FAQSection';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import { ServiceCard, ProjectCard } from '../components/cards';
import { getLocation } from '../data/locations';
import { LOCATION_SECTIONS } from '../data/locationSections';
import { SERVICES } from '../data/services';
import { PROJECTS } from '../data/projects';
import { SITE } from '../data/site';
import NotFoundPage from './NotFoundPage';

const LocationPage = () => {
  const { slug } = useParams();
  const loc = getLocation(slug);
  if (!loc) return <NotFoundPage />;

  const nearby = loc.nearby.map(getLocation).filter(Boolean);
  const featuredServices = SERVICES.filter((s) => ['patios', 'outdoor-kitchens', 'pergolas', 'fire-features'].includes(s.slug));
  const cityServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Outdoor Living Design & Build in ${loc.city}, NE`,
    description: loc.metaDescription,
    provider: { '@type': 'HomeAndConstructionBusiness', name: SITE.name, telephone: SITE.phone },
    areaServed: `${loc.city}, Nebraska`,
    url: `${SITE.baseUrl}/service-areas/${loc.slug}/`,
  };

  return (
    <>
      <SEO
        title={loc.metaTitle}
        description={loc.metaDescription}
        path={`/service-areas/${loc.slug}/`}
        image={loc.image.src}
        jsonLd={[
          cityServiceSchema,
          faqSchema(loc.faqs),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Service Areas', path: '/service-areas/' }, { name: loc.city, path: `/service-areas/${loc.slug}/` }]),
        ]}
      />
      <PageHero
        overline={`Serving ${loc.region}`}
        title={loc.headline}
        intro={loc.intro[0]}
        image={loc.image}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Service Areas', path: '/service-areas/' }, { name: loc.city }]}
      />

      <section className="py-20 md:py-24">
        <div className="container-site grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">{loc.localHeading}</h2>
              <p className="mt-5 text-base leading-relaxed text-muted2 md:text-lg">{loc.intro[1]}</p>
              <ul className="mt-8 space-y-4">
                {loc.localPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 border-l-2 border-bronze bg-paper2/50 px-5 py-4 text-sm leading-relaxed text-ink/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" /> {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="noise-dark relative border border-line bg-graphite p-8 text-paper">
                <h3 className="relative z-10 font-serif text-2xl font-medium">Free consultation in {loc.city}</h3>
                <p className="relative z-10 mt-4 text-sm leading-relaxed text-paper/70">
                  We come to you. Tell us how you want to live outdoors and we'll design around your property, your style, and your budget — with transparent pricing before any work begins.
                </p>
                <Link to="/get-a-quote/" data-testid={`location-${loc.slug}-quote-btn`} className="btn-primary relative z-10 mt-7 w-full">Get a Free Quote</Link>
                <a href={SITE.phoneHref} data-testid={`location-${loc.slug}-call-link`} className="relative z-10 mt-4 block text-center text-sm font-semibold text-bronzelight transition-colors hover:text-paper">
                  Or call {SITE.phone}
                </a>
              </div>
              <div className="mt-6 border border-line p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">Nearby communities we serve</h3>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {nearby.map((n) => (
                    <li key={n.slug}>
                      <Link to={`/service-areas/${n.slug}/`} data-testid={`nearby-${n.slug}`} className="inline-block border border-line px-4 py-2 text-sm text-ink/70 transition-colors hover:border-bronze hover:text-bronze">
                        {n.city}, NE
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-paper2/50 py-20 md:py-24">
        <div className="container-site max-w-4xl">
          {(LOCATION_SECTIONS[loc.slug] || []).map((sec, i) => (
            <Reveal key={sec.h} className={i > 0 ? 'mt-12' : ''}>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">{sec.h}</h2>
              {sec.body?.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-muted2 md:text-lg">{p}</p>
              ))}
              {sec.list && (
                <ul className="mt-5 space-y-3">
                  {sec.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-base text-muted2">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-bronze" /> {item}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-site">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Outdoor living services in {loc.city}</h2>
            <p className="mt-4 max-w-2xl text-base text-muted2">Custom design and construction for {loc.city} homeowners — explore our most-requested services or browse the full range.</p>
          </Reveal>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featuredServices.map((s) => <StaggerItem key={s.slug}><ServiceCard service={s} /></StaggerItem>)}
          </Stagger>
          <Reveal className="mt-10">
            <Link to="/services/" data-testid="location-all-services" className="link-underline text-sm uppercase tracking-[0.2em]">View All Services <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-site">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">The kind of work we build</h2>
          </Reveal>
          <Stagger className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.slice(0, 3).map((p) => <StaggerItem key={p.slug}><ProjectCard project={p} /></StaggerItem>)}
          </Stagger>
        </div>
      </section>

      <section className="pb-20 md:pb-24">
        <div className="container-site max-w-4xl">
          <Reveal><FAQSection faqs={loc.faqs} heading={`${loc.city} outdoor living FAQs`} /></Reveal>
        </div>
      </section>

      <CTASection title={`Let's design your ${loc.city} backyard`} text={`Free on-site consultations throughout ${loc.city} and ${loc.region.toLowerCase()}. 22+ years of Nebraska craftsmanship, one accountable team.`} testId={`location-${loc.slug}-cta`} />
    </>
  );
};

export default LocationPage;
